import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../api/client.js'

interface DashboardSummary {
  todays_bookings: number
  vehicles_running: number
  pending_dispatch: number
  pending_pod: number
  pending_billing: number
  alerts_count: number
  customer_outstanding: string
  vendor_outstanding: string
  broker_outstanding: string
  petty_cash_position: string
  collections_today: string
  payments_today: string
  monthly_revenue: string
  monthly_profit: string
}

function formatMoney(value: string): string {
  const n = Number(value)
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

/** Stage 3 §4.1/§4.2 layout and widget → data-source mapping, wired to the real API. */
export function DashboardPage() {
  const { t } = useTranslation()
  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .get<DashboardSummary>('/dashboard/summary')
      .then((data) => {
        if (!cancelled) setSummary(data)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load dashboard')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const kpis: Array<{ key: keyof DashboardSummary; label: string }> = [
    { key: 'todays_bookings', label: t('dashboard.todaysBookings') },
    { key: 'vehicles_running', label: t('dashboard.vehiclesRunning') },
    { key: 'pending_pod', label: t('dashboard.pendingPod') },
    { key: 'pending_billing', label: t('dashboard.pendingBilling') },
  ]

  return (
    <div>
      <div className="tf-kpi-row">
        {kpis.map(({ key, label }) => (
          <div className="tf-kpi" key={key}>
            <div className="tf-kpi-label">{label}</div>
            <div className="tf-kpi-value">{summary ? summary[key] : '—'}</div>
          </div>
        ))}
      </div>

      {error && <p className="tf-error">{error}</p>}

      <div className="tf-dashboard-grid">
        <section className="tf-card">
          <h2>{t('dashboard.outstanding')}</h2>
          <dl className="tf-stat-list">
            <div>
              <dt>{t('dashboard.customerOutstanding')}</dt>
              <dd>{summary ? formatMoney(summary.customer_outstanding) : '—'}</dd>
            </div>
            <div>
              <dt>{t('dashboard.vendorOutstanding')}</dt>
              <dd>{summary ? formatMoney(summary.vendor_outstanding) : '—'}</dd>
            </div>
            <div>
              <dt>{t('dashboard.brokerOutstanding')}</dt>
              <dd>{summary ? formatMoney(summary.broker_outstanding) : '—'}</dd>
            </div>
          </dl>
        </section>

        <section className="tf-card">
          <h2>{t('dashboard.cashAndCollections')}</h2>
          <dl className="tf-stat-list">
            <div>
              <dt>{t('dashboard.pettyCashPosition')}</dt>
              <dd>{summary ? formatMoney(summary.petty_cash_position) : '—'}</dd>
            </div>
            <div>
              <dt>{t('dashboard.collectionsToday')}</dt>
              <dd>{summary ? formatMoney(summary.collections_today) : '—'}</dd>
            </div>
            <div>
              <dt>{t('dashboard.paymentsToday')}</dt>
              <dd>{summary ? formatMoney(summary.payments_today) : '—'}</dd>
            </div>
          </dl>
        </section>

        <section className="tf-card">
          <h2>{t('dashboard.monthlySnapshot')}</h2>
          <dl className="tf-stat-list">
            <div>
              <dt>{t('dashboard.revenue')}</dt>
              <dd>{summary ? formatMoney(summary.monthly_revenue) : '—'}</dd>
            </div>
            <div>
              <dt>{t('dashboard.profit')}</dt>
              <dd>{summary ? formatMoney(summary.monthly_profit) : '—'}</dd>
            </div>
          </dl>
        </section>

        <section className="tf-card">
          <h2>{t('dashboard.alerts')}</h2>
          <p className="tf-note">
            {summary ? summary.alerts_count : '—'} unacknowledged.
            {/* The scheduled scanner that populates alerts (Stage 2 §5.1) is not
                built in this pass — see README — so this is usually 0. */}
          </p>
        </section>
      </div>
    </div>
  )
}
